/**
 * BottomNav.tsx
 * Phase 1p Mobile Nav + A11y SSOT — Wave 3 Stage 2
 * Issue #3028
 *
 * Mobile-first Bottom Navigation Bar (md 未満専用)
 * 5 項目: Dashboard / 予約 / 顧客 / カルテ / その他
 * ICP オーナーアクセス頻度順
 *
 * A11y:
 * - role="navigation" + aria-label="メインナビゲーション"
 * - .ripia-focus-ring utility クラス適用 (Phase 1p SSOT)
 * - URL-based active state (useLocation)
 *
 * Scope OUT: gesture / PWA / オフライン (Tier 2)
 */

import * as React from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Users,
  ClipboardList,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsRepeatFirstOwner } from '@/hooks/repeat/useIsRepeatFirstOwner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BottomNavProps {
  /** Drawer を開くコールバック */
  onOpenDrawer: () => void;
}

interface NavLinkItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
}

interface NavActionItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

type NavItem = NavLinkItem | NavActionItem;

function isNavLink(item: NavItem): item is NavLinkItem {
  return 'to' in item;
}

// ---------------------------------------------------------------------------
// BottomNav component
// ---------------------------------------------------------------------------

/**
 * BottomNav
 *
 * mobile viewport (md 未満) にのみ表示する Bottom Navigation。
 * 5 項目固定: Dashboard / 予約 / 顧客 / カルテ / その他
 *
 * @example
 * ```tsx
 * <BottomNav onOpenDrawer={() => setDrawerOpen(true)} />
 * ```
 */
export function BottomNav({
  onOpenDrawer,
}: BottomNavProps): React.ReactElement {
  const location = useLocation();
  // #4038 / HAM-3471 案B: repeat-first owner には /dashboard 導線を出さない
  // （gate で /repeat へ bounce されるだけの導線を隠す cosmetic）。staff は維持。
  const isRepeatFirstOwner = useIsRepeatFirstOwner();

  const allNavItems: NavItem[] = [
    {
      label: 'ダッシュボード',
      icon: LayoutDashboard,
      to: '/dashboard',
    },
    {
      label: '予約',
      icon: Calendar,
      to: '/reservations',
    },
    {
      label: '顧客',
      icon: Users,
      to: '/customers',
    },
    {
      label: 'カルテ',
      icon: ClipboardList,
      to: '/treatments',
    },
    {
      label: 'その他',
      icon: Menu,
      onClick: onOpenDrawer,
    },
  ];

  const navItems = allNavItems.filter(
    (item) =>
      !(isNavLink(item) && item.to === '/dashboard' && isRepeatFirstOwner)
  );

  return (
    <nav
      role="navigation"
      aria-label="メインナビゲーション"
      data-testid="bottom-nav"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40',
        'flex h-16 items-stretch',
        'bg-[var(--paper)] border-t border-[var(--line)]',
        // Safe area padding for iOS home indicator
        'pb-[env(safe-area-inset-bottom,0px)]'
      )}
    >
      {navItems.map((item) => {
        const Icon = item.icon;

        if (isNavLink(item)) {
          // URL-based active state: path starts with nav item's route
          const isActive =
            item.to === '/dashboard'
              ? location.pathname === '/dashboard' || location.pathname === '/'
              : location.pathname.startsWith(item.to);

          return (
            <Link
              key={item.label}
              to={item.to}
              className={cn(
                'ripia-focus-ring',
                'flex flex-1 flex-col items-center justify-center gap-0.5',
                'min-w-0 text-xs font-medium',
                'transition-colors duration-[var(--dur-fast)]',
                isActive
                  ? 'text-[var(--brand)]'
                  : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon
                className={cn(
                  'h-5 w-5 shrink-0',
                  isActive ? 'text-[var(--brand)]' : 'text-[var(--ink-3)]'
                )}
              />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        }

        // Action item (その他 → opens Drawer)
        return (
          <button
            key={item.label}
            type="button"
            onClick={item.onClick}
            className={cn(
              'ripia-focus-ring',
              'flex flex-1 flex-col items-center justify-center gap-0.5',
              'min-w-0 text-xs font-medium',
              'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
              'transition-colors duration-[var(--dur-fast)]'
            )}
            aria-label={`${item.label}メニューを開く`}
          >
            <Icon className="h-5 w-5 shrink-0 text-[var(--ink-3)]" />
            <span className="truncate">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
